import type { Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { withFrappeLoading } from "@/services/frappe/list";
import { Controllers, FrappeMethodType } from "@/type/api";
import type { AccountingSettings } from "@/type/accountingSettings";

const accountingSettingsService = new HttpClient(Controllers.account);

const GET_ACCOUNTING_SETTINGS_METHOD = "pet_app.api.accounting.settings.get_accounting_settings";
const UPDATE_ACCOUNTING_SETTINGS_METHOD = "pet_app.api.accounting.settings.update_accounting_settings";

function normalizeString(value: unknown) {
  const normalized = String(value ?? "").trim();
  return normalized || null;
}

function normalizeInteger(value: unknown) {
  if (value === null || value === undefined || value === "")
    return null;

  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : null;
}

function unwrapMethodPayload<T>(response: any): T {
  return (response?.message ?? response?.data ?? response ?? null) as T;
}

function mapAccountingSettings(payload: any): AccountingSettings {
  return {
    defaultCompany: normalizeString(payload?.default_company ?? payload?.defaultCompany),
    treasuryCashAccount: normalizeString(payload?.treasury_cash_account ?? payload?.treasuryCashAccount),
    defaultCashModeOfPayment: normalizeString(payload?.default_cash_mode_of_payment ?? payload?.defaultCashModeOfPayment),
    cashOverShortAccount: normalizeString(payload?.cash_over_short_account ?? payload?.cashOverShortAccount),
    salesInvoiceDueDays: normalizeInteger(payload?.sales_invoice_due_days ?? payload?.salesInvoiceDueDays),
  };
}

export async function GetAccountingSettings(
  loading: Ref<boolean>,
): Promise<AccountingSettings> {
  return withFrappeLoading(loading, async () => {
    const response = await accountingSettingsService.get<any>(
      GET_ACCOUNTING_SETTINGS_METHOD,
      {},
      {},
      undefined,
      undefined,
      FrappeMethodType.method,
    );

    return mapAccountingSettings(unwrapMethodPayload(response));
  });
}

export async function UpdateAccountingSettings(
  loading: Ref<boolean>,
  payload: Partial<AccountingSettings>,
): Promise<AccountingSettings> {
  return withFrappeLoading(loading, async () => {
    const response = await accountingSettingsService.post<any>(
      {
        default_company: payload.defaultCompany ?? null,
        treasury_cash_account: payload.treasuryCashAccount ?? null,
        default_cash_mode_of_payment: payload.defaultCashModeOfPayment ?? null,
        cash_over_short_account: payload.cashOverShortAccount ?? null,
        sales_invoice_due_days: payload.salesInvoiceDueDays ?? null,
      },
      UPDATE_ACCOUNTING_SETTINGS_METHOD,
      {},
      undefined,
      FrappeMethodType.method,
    );

    return mapAccountingSettings(unwrapMethodPayload(response));
  });
}
